const PROCEDURES = [
  { kind: 'word', skillCode: 'document.word.create', label: '制作 Word' },
  { kind: 'ppt', skillCode: 'document.ppt.create', label: '制作 PPT' },
  { kind: 'excel-analyze', skillCode: 'spreadsheet.excel.process', label: '分析 Excel' },
  { kind: 'excel-create', skillCode: 'spreadsheet.excel.process', label: '制作 Excel' }
]

export function availableArtifactActions(bindings, runtimeEngine) {
  if (runtimeEngine !== 'mastra' || !Array.isArray(bindings)) return []
  return PROCEDURES.filter(action => bindings.some(binding =>
    binding?.capabilityId === action.skillCode && binding.version === 'v1' && binding.enabled !== false))
}

export function isArtifactJobActive(status) {
  return !['completed', 'failed', 'cancelled'].includes(status)
}

export function reconcileArtifactJobList(serverJobs, localJobs) {
  const server = Array.isArray(serverJobs) ? serverJobs : []
  const seen = new Set(server.map(job => job.jobId))
  return server.concat((Array.isArray(localJobs) ? localJobs : [])
    .filter(job => job?.jobId && !seen.has(job.jobId)))
}

export function jobsForAssistantMessage(jobs, message) {
  if (message?.role !== 'assistant' || !message.executionId) return []
  return (Array.isArray(jobs) ? jobs : []).filter(job => job?.jobId
    && job.executionId === message.executionId)
}

export function orphanArtifactJobs(jobs, messages) {
  const visibleExecutions = new Set((Array.isArray(messages) ? messages : [])
    .filter(message => message?.role === 'assistant' && message.executionId)
    .map(message => message.executionId))
  return (Array.isArray(jobs) ? jobs : []).filter(job => job?.jobId
    && (!job.executionId || !visibleExecutions.has(job.executionId)))
}

export function usableArtifactExecutionId(result) {
  return result?.terminalType === 'run_completed' && typeof result.executionId === 'string'
    ? result.executionId : ''
}

export function eligibleWorkbookAssets(messages) {
  const assets = (Array.isArray(messages) ? messages : [])
    .flatMap(message => Array.isArray(message?.attachments) ? message.attachments : [])
    .filter(asset => asset?.assetId && asset.status === 'active' && /\.xlsx$/i.test(asset.name || ''))
  return Array.from(new Map(assets.map(asset => [asset.assetId, asset])).values())
}

function lines(content) {
  return String(content || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean)
}

function sectionName(line) {
  const text = line.trim()
  const unwrapped = text.replace(/^【\s*/, '').replace(/\s*】$/, '')
  const direct = unwrapped.match(/^\s*(?:#{1,6}\s*)?(?:Sheet|工作表)\s*\d+\s*[:：]\s*(.+?)\s*$/i)
  if (direct) return direct[1].trim()
  const chinese = text.match(/^[一二三四五六七八九十]+、\s*([^（]+)(?:（([^）]*?)\s+Sheet[^）]*）)?$/i)
  return chinese ? (chinese[2] || chinese[1]).trim() : null
}

export function reviewDraftFromDetail(detail, localDraft) {
  const approved = detail?.job?.status === 'waiting_final_approval'
    ? detail?.reviewDraft?.approvedDraft : null
  if (typeof approved?.title === 'string' && approved.title.trim()
    && typeof approved?.content === 'string'
    && (approved.content.trim() || approved.canRevise === false)) {
    return { title: approved.title, content: approved.content,
      ...(approved.canRevise === false ? { canRevise: false } : {}),
      ...(typeof approved.subtitle === 'string' && approved.subtitle
        ? { spec: { subtitle: approved.subtitle } } : {}) }
  }
  if (detail?.job?.status === 'waiting_final_approval'
    && ['docx', 'pptx'].includes(detail?.job?.format)) {
    throw new Error('已批准内容暂不可读取，请稍后重试')
  }
  if (localDraft?.title?.trim() && localDraft?.content?.trim()) {
    return { title: localDraft.title, content: localDraft.content,
      ...(localDraft.spec ? { spec: cloneWorkbookSpec(localDraft.spec) } : {}) }
  }
  const content = typeof detail?.reviewDraft?.brief === 'string' ? detail.reviewDraft.brief.trim() : ''
  const spec = detail?.job?.format === 'xlsx' && detail?.reviewDraft?.draftSpec
    ? cloneWorkbookSpec(detail.reviewDraft.draftSpec) : null
  const first = lines(content)[0] || ''
  const proposed = (spec?.sheets?.[0]?.title || spec?.sheets?.[0]?.name
    || sectionName(first) || (detail?.job?.format === 'xlsx'
    ? first.match(/《([^》]+)》/)?.[1] : null) || first.replace(/^#{1,6}\s*/, '')).slice(0, 80)
  const title = detail?.job?.format === 'xlsx'
    && (!validSheetName(proposed) || /^(?:[-*•]\s+|\d+[.、)]\s+)/.test(proposed))
    ? '工作表1' : proposed
  return { title, content, ...(spec ? { spec } : {}) }
}

function cloneWorkbookSpec(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !Array.isArray(raw.sheets)
    || raw.sheets.length < 1 || raw.sheets.length > 20) {
    throw new Error('artifact_draft_invalid')
  }
  return JSON.parse(JSON.stringify(raw))
}

function parseMarkdownTable(content, sheetName) {
  const table = lines(content).filter(line => /^\|.*\|$/.test(line))
  if (table.length < 3) throw new Error('artifact_table_required')
  const cells = line => line.slice(1, -1).split('|').map(value => value.trim())
  const headers = cells(table[0])
  if (headers.length < 1 || headers.length > 30 || headers.some(name => !name || name.length > 80)
    || cells(table[1]).length !== headers.length
    || cells(table[1]).some(value => !/^:?-{3,}:?$/.test(value))) {
    throw new Error('artifact_table_invalid')
  }
  const rows = table.slice(2).map(cells)
  if (rows.length > 5000 || rows.some(row => row.length !== headers.length)) {
    throw new Error('artifact_table_invalid')
  }
  const kind = (index) => {
    const values = rows.map(row => row[index]).filter(Boolean)
    if (values.length && values.every(value => /^-?(?:\d+)(?:\.\d+)?$/.test(value))) return 'number'
    if (values.length && values.every(value => /^\d{4}-\d{2}-\d{2}$/.test(value))) return 'date'
    return 'text'
  }
  const columns = headers.map((name, index) => ({ name, type: kind(index) }))
  const typedRows = rows.map(row => row.map((value, index) =>
    value === '' ? null : columns[index].type === 'number' ? Number(value) : value))
  if (typedRows.some(row => row.some(value => typeof value === 'number' && !Number.isFinite(value)))) {
    throw new Error('artifact_table_invalid')
  }
  return { sheets: [{ name: sheetName, columns, rows: typedRows }] }
}

function validSheetName(name) {
  return name.length > 0 && name.length <= 31 && !/[\[\]:*?/\\]/.test(name)
    && !name.startsWith("'") && !name.endsWith("'")
}

function bulletSheet(content, name) {
  const items = lines(content).map(line => line.match(/^(?:[-*•]\s+|\d+[.、)]\s+)(.+)$/)?.[1])
    .filter(Boolean)
  if (!items.length || items.length > 5000) throw new Error('artifact_table_required')
  return { name, columns: [{ name: '内容', type: 'text' }], rows: items.map(item => [item]) }
}

function pipeSheet(content, name) {
  const source = lines(content)
  const headerPattern = /^(?:\d+[.、)]\s*)?(?:表头列|列建议|表头)\s*[:：]/
  const headerLine = source.find(line => headerPattern.test(line))
  if (!headerLine) return null
  const headers = headerLine.replace(headerPattern, '').trim().split('|').map(cell => cell.trim())
  if (!headers.length || headers.length > 30 || headers.some(cell => !cell || cell.length > 80)) {
    throw new Error('artifact_table_invalid')
  }
  const rawRows = []
  for (const line of source.slice(source.indexOf(headerLine) + 1)) {
    const value = line.replace(/^\d+[.、)]\s*数据行[^：:]*[:：]\s*/, '')
      .replace(/^数据行[^：:]*[:：]\s*/, '').replace(/^[-*•]\s+/, '')
    if (/^\d+[.、)]\s*(?:合计行|总计行|备注行|格式要求)\s*[:：]/.test(value)) continue
    if (value.includes('|')) rawRows.push(value.split('|').map(cell => cell.trim()))
    else {
      const numbered = value.match(/^(\d+)[.、]\s*([^：:]+)[：:]\s*(.+)$/)
      if (numbered && headers.length >= 3) {
        rawRows.push([numbered[1], numbered[2], numbered[3],
          ...Array(headers.length - 3).fill('')])
      }
    }
  }
  if (!rawRows.length || rawRows.length > 5000 || rawRows.some(row => row.length !== headers.length)) {
    throw new Error('artifact_table_invalid')
  }
  const normalized = rawRows.map(row => row.map(value => /^(?:（预留）|\(预留\))$/.test(value) ? '' : value))
  const columns = headers.map((column, index) => {
    const values = normalized.map(row => row[index]).filter(Boolean)
    const type = values.length && values.every(value => /^-?\d+(?:\.\d+)?$/.test(value.replace(/,/g, ''))) ? 'number'
      : values.length && values.every(value => /^\d{4}-\d{2}-\d{2}$/.test(value)) ? 'date' : 'text'
    return { name: column, type }
  })
  const rows = normalized.map(row => row.map((value, index) => !value ? null
    : columns[index].type === 'number' ? Number(value.replace(/,/g, '')) : value))
  const result = { name, columns, rows }
  const title = String(content).match(/标题行\s*[:：]\s*[「“"]([^」”"]+)[」”"]/)?.[1]?.trim()
  if (title) result.title = title.slice(0, 200)
  const totalRequested = /(?:合计行|总计行)[\s\S]{0,200}(?:SUM\s*\(|自动求和|金额列)/i.test(content)
  if (totalRequested) {
    const formulaColumn = String(content).match(/SUM\s*\(\s*([A-Z]+)\d+\s*:/i)?.[1]
    const columnIndex = formulaColumn
      ? formulaColumn.toUpperCase().split('').reduce((value, char) => value * 26 + char.charCodeAt(0) - 64, 0) - 1
      : columns.findIndex(column => column.type === 'number' && /金额|价格|费用|总价/.test(column.name))
    if (columnIndex > 0 && columnIndex < columns.length && columns[columnIndex].type === 'number') {
      result.sumColumns = [columnIndex]
      result.totalLabelSpan = columnIndex
    }
  }
  return result
}

function keyValueSheet(content, name) {
  const rows = String(content).split(/\r?\n/).map(line =>
    line.match(/^\s*[-*•]\s+([^：:]+)[：:]\s*(.+)\s*$/))
    .filter(Boolean).map(match => [match[1].trim(), match[2].trim()])
  if (!rows.length) return null
  if (rows.length > 5000) throw new Error('artifact_table_invalid')
  return { name, columns: [{ name: '项目', type: 'text' }, { name: '内容', type: 'text' }], rows }
}

function parseWorkbook(content, fallbackName) {
  const sections = []
  for (const line of String(content).split(/\r?\n/)) {
    if (/^\s*(?:【格式要求】|格式要求\s*[:：])/.test(line)) break
    const name = sectionName(line)
    if (name) sections.push({ name, content: [] })
    else if (sections.length) sections[sections.length - 1].content.push(line)
  }
  if (!sections.length) {
    return lines(content).some(line => /^\|.*\|$/.test(line))
      ? parseMarkdownTable(content, fallbackName)
      : { sheets: [bulletSheet(content, fallbackName)] }
  }
  if (sections.length > 20 || sections.some(section => !validSheetName(section.name))
    || new Set(sections.map(section => section.name.toLowerCase())).size !== sections.length) {
    throw new Error('artifact_sheet_name_invalid')
  }
  const sheets = sections.map(section => {
    const body = section.content.join('\n')
    if (lines(body).some(line => /^\|.*\|$/.test(line))) {
      return parseMarkdownTable(body, section.name).sheets[0]
    }
    return pipeSheet(body, section.name) || keyValueSheet(body, section.name)
      || bulletSheet(body, section.name)
  })
  return { sheets }
}

function wordSections(content) {
  const sections = []
  let current = { heading: '正文', blocks: [] }
  let paragraph = []
  let bullets = []
  const flushParagraph = () => {
    if (paragraph.length) current.blocks.push({ type: 'paragraph', text: paragraph.join('\n') })
    paragraph = []
  }
  const flushBullets = () => {
    if (bullets.length) current.blocks.push({ type: 'bullets', items: bullets })
    bullets = []
  }
  const flushSection = () => {
    flushParagraph()
    flushBullets()
    if (current.blocks.length) sections.push(current)
  }
  for (const rawLine of String(content).split(/\r?\n/)) {
    const line = rawLine.trim()
    const heading = line.match(/^#{1,3}\s+(.+)$/)
    const bullet = line.match(/^[-*•]\s+(.+)$/)
    if (heading) {
      flushSection()
      if (heading[1].length > 200) throw new Error('artifact_content_too_long')
      current = { heading: heading[1], blocks: [] }
    } else if (!line) {
      flushParagraph()
      flushBullets()
    } else if (bullet) {
      flushParagraph()
      if (bullet[1].length > 1000) throw new Error('artifact_content_too_long')
      bullets.push(bullet[1])
      if (bullets.length === 50) flushBullets()
    } else {
      flushBullets()
      paragraph.push(line)
    }
  }
  flushSection()
  if (sections.length > 30 || sections.reduce((count, section) => count + section.blocks.length, 0) > 100) {
    throw new Error('artifact_content_too_long')
  }
  if (!sections.length) throw new Error('artifact_content_required')
  return sections
}

function pptItems(line) {
  // 固定模板的正文盒只容纳约 35 个中文字符；在提交审批前分行，避免 Worker 裁切内容。
  const result = []
  for (let index = 0; index < line.length; index += 35) result.push(line.slice(index, index + 35))
  return result
}

function pptSlides(content, title) {
  const sections = [{ title: '要点', items: [] }]
  for (const line of lines(content)) {
    const heading = line.match(/^#{1,3}\s+(.+)$/)
      || line.match(/^第[一二三四五六七八九十\d]+页[：:]\s*(.+)$/)
    if (heading) {
      if (heading[1].length > 80) throw new Error('artifact_content_too_long')
      sections.push({ title: heading[1], items: [] })
      continue
    }
    const body = line.replace(/^[-*•]\s+/, '')
    sections[sections.length - 1].items.push(...pptItems(body))
  }
  const slides = [{ type: 'title', title, subtitle: '' }]
  for (const section of sections) {
    for (let index = 0; index < section.items.length; index += 6) {
      slides.push({ type: 'bullets', title: section.title, items: section.items.slice(index, index + 6) })
    }
  }
  if (slides.length < 2 || slides.length > 20) throw new Error('artifact_content_too_long')
  return slides
}

/** Structured confirmation input derived from a user-reviewed chat draft, never arbitrary code. */
export function buildArtifactSpec(kind, rawTitle, rawContent) {
  const title = String(rawTitle || '').trim()
  const content = String(rawContent || '').trim()
  if (!title || !content) throw new Error('artifact_content_required')
  if (content.length > 10000) throw new Error('artifact_content_too_long')
  if (kind === 'word') {
    if (title.length > 200 || content.length > 10000) throw new Error('artifact_content_too_long')
    return { title, sections: wordSections(content) }
  }
  if (kind === 'ppt') {
    // 项目审批的分节标题与已上线的长文本分行共用同一解析器，避免合并后退回扁平内容。
    if (title.length > 80) throw new Error('artifact_content_too_long')
    return { title, slides: pptSlides(content, title) }
  }
  if (kind === 'excel-create') {
    if (!validSheetName(title)) {
      throw new Error('artifact_sheet_name_invalid')
    }
    return parseWorkbook(content, title)
  }
  throw new Error('artifact_kind_invalid')
}

/** Uses a model-produced, server-validated workbook draft without reparsing its prose brief. */
export function buildArtifactApprovalSpec(kind, rawTitle, sourceContent, currentContent, draftSpec) {
  if (kind === 'excel-create' && draftSpec) {
    if (String(sourceContent || '').trim() !== String(currentContent || '').trim()) {
      throw new Error('artifact_draft_changed')
    }
    return cloneWorkbookSpec(draftSpec)
  }
  const spec = buildArtifactSpec(kind, rawTitle, currentContent)
  if (kind === 'ppt' && typeof draftSpec?.subtitle === 'string') {
    spec.slides[0].subtitle = draftSpec.subtitle
  }
  return spec
}

export function artifactApprovalErrorMessage(kind, error) {
  if (error?.message === 'artifact_content_required') return '请填写标题与正文内容后再确认'
  if (error?.message === 'artifact_content_too_long') {
    return kind === 'ppt' ? '演示内容过长，最多生成 20 页；请精简或拆分为多个任务'
      : '文档内容过长，请精简正文或拆分为多个任务'
  }
  return '内容格式暂不支持，请检查后重试'
}

/** Show the reviewer the same bounded arithmetic that the fixed Excel renderer will apply. */
export function summarizeExcelApprovalSpec(spec, maxRows = 20) {
  if (!spec || !Array.isArray(spec.sheets)) return []
  return spec.sheets.map(sheet => {
    const columns = Array.isArray(sheet.columns) ? sheet.columns.map(column => column.name) : []
    const rules = Array.isArray(sheet.calculatedColumns) ? sheet.calculatedColumns : []
    const computedRows = (Array.isArray(sheet.rows) ? sheet.rows : []).map(source => {
      const row = [...source]
      for (const rule of rules) {
        if (rule.operation === 'multiply') {
          const [left, right] = rule.operandColumns
          row[rule.targetColumn] = source[left] * source[right]
        }
      }
      return row
    })
    const total = Array.isArray(sheet.sumColumns) && sheet.sumColumns.length
      ? columns.map((_, index) => index === 0 ? '合计'
        : sheet.sumColumns.includes(index)
          ? computedRows.reduce((sum, row) => sum + (Number(row[index]) || 0), 0) : null)
      : null
    return {
      name: sheet.name, columns, rows: computedRows.slice(0, Math.max(0, Math.min(30, maxRows))),
      truncated: computedRows.length > maxRows, total,
      calculations: rules.map(rule => `${columns[rule.targetColumn]} = `
        + `${columns[rule.operandColumns[0]]} × ${columns[rule.operandColumns[1]]}`)
    }
  })
}

/** Applies only bounded Excel layout intents; unsupported free-form requests fail closed. */
export function buildArtifactRevisionSpec(kind, rawTitle, sourceContent, rawInstruction, draftSpec = null) {
  if (kind !== 'excel-create') throw new Error('artifact_revision_instruction_unsupported')
  const instruction = String(rawInstruction || '').trim()
  if (!instruction || instruction.length > 2000) throw new Error('artifact_revision_instruction_unsupported')
  const spec = draftSpec ? cloneWorkbookSpec(draftSpec)
    : buildArtifactSpec(kind, rawTitle, sourceContent)
  const wantsTotal = /合计|总计|求和|SUM/i.test(instruction)
  const wantsMergedTotal = wantsTotal && /合并单元格|合并.*(?:列|格)/.test(instruction)
  const dropsNarrative = /(?:不要|删除|去掉|移除)[\s\S]{0,30}(?:备注|文案|说明)/.test(instruction)
  if (!wantsTotal && !dropsNarrative) throw new Error('artifact_revision_instruction_unsupported')
  if (wantsTotal) {
    for (const sheet of spec.sheets) {
      let index = sheet.columns.findIndex(column => column.type === 'number'
        && /金额|价格|费用|总价|小计/.test(column.name))
      if (index <= 0) {
        for (let current = sheet.columns.length - 1; current > 0; current--) {
          if (sheet.columns[current].type === 'number') { index = current; break }
        }
      }
      if (index > 0 && sheet.rows.length) {
        sheet.sumColumns = [index]
        if (wantsMergedTotal) sheet.totalLabelSpan = index
      }
    }
    if (!spec.sheets.some(sheet => Array.isArray(sheet.sumColumns) && sheet.sumColumns.length)) {
      throw new Error('artifact_revision_instruction_unsupported')
    }
  }
  return spec
}
